import { Fragment } from "@tiptap/pm/model";
export const FULL_TOOLBAR = [
    "blocks",
    "bold",
    "italic",
    "lists",
    "color",
    "image",
    "media",
    "link",
    "align",
    "hr",
    "emoji",
    "code",
    "history",
];
export const SIMPLE_TOOLBAR = ["bold", "italic", "lists", "history"];
export const EMAIL_TOOLBAR = [
    "blocks",
    "bold",
    "italic",
    "lists",
    "image",
    "link",
    "align",
    "history",
];
export const MESSAGE_TOOLBAR = [
    "blocks",
    "bold",
    "italic",
    "lists",
    "color",
    "image",
    "link",
    "align",
    "hr",
    "history",
];
// Only features that survive HTML→Markdown conversion
export const MARKDOWN_TOOLBAR = [
    "blocks", "bold", "italic", "lists", "link", "hr", "history",
];
export const CAMPAIGN_UPDATE_TOOLBAR = [
    "blocks",
    "bold",
    "italic",
    "lists",
    "color",
    "link",
    "align",
    "hr",
    "emoji",
    "code",
    "history",
];
// --- Colour palette ---
// All colours verified ≥ 4.5:1 contrast ratio against white (WCAG AA)
export const COLORS = [
    // Neutrals
    "#000000",
    "#212121",
    "#424242",
    "#616161",
    "#4e342e",
    "#37474f",
    "#1a237e",
    "#004d40",
    // Dark saturated
    "#b71c1c",
    "#bf360c",
    "#6d4c00",
    "#1b5e20",
    "#005662",
    "#0d47a1",
    "#4a148c",
    "#880e4f",
    // Medium (still accessible)
    "#c62828",
    "#b34000",
    "#7b6300",
    "#2e7d32",
    "#006374",
    "#1565c0",
    "#6a1b9a",
    "#ad1457",
];
// --- Emoji list ---
export const EMOJIS = [
    "😀",
    "😂",
    "😍",
    "🥰",
    "😊",
    "🤔",
    "😢",
    "😮",
    "👍",
    "👎",
    "🙏",
    "👏",
    "💪",
    "🤝",
    "❤️",
    "🔥",
    "✨",
    "🎉",
    "🥳",
    "🌟",
    "💯",
    "🎊",
    "🙌",
    "🤩",
];
// --- Document colour extraction ---
function rgbToHex(r, g, b) {
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}
export function extractDocumentColors(html, existingColors = COLORS) {
    const found = new Set();
    const existing = new Set(existingColors.map((color) => color.toLowerCase()));
    Array.from(html.matchAll(/color:\s*(#[0-9a-fA-F]{3,8}|rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\))/gi)).forEach((match) => {
        const color = match[2] !== undefined
            ? rgbToHex(parseInt(match[2], 10), parseInt(match[3], 10), parseInt(match[4], 10)).toLowerCase()
            : match[1].toLowerCase();
        if (!existing.has(color)) {
            found.add(color);
        }
    });
    return Array.from(found);
}
// --- Video embed helpers ---
export const ALLOWED_VIDEO_HOSTS = new Set([
    "www.youtube.com",
    "youtube.com",
    "youtu.be",
    "www.youtu.be",
    "www.youtube-nocookie.com",
    "youtube-nocookie.com",
    "player.vimeo.com",
    "www.vimeo.com",
    "vimeo.com",
]);
const YOUTUBE_NOCOOKIE_EMBED_BASE = "https://www.youtube-nocookie.com/embed/";
export function isApprovedEmbedUrl(url) {
    try {
        const { protocol, hostname, pathname } = new URL(url);
        if (protocol !== "https:") {
            return false;
        }
        if (!ALLOWED_VIDEO_HOSTS.has(hostname)) {
            return false;
        }
        const isYouTubeEmbed = (hostname === "www.youtube.com"
            || hostname === "youtube.com"
            || hostname === "www.youtube-nocookie.com"
            || hostname === "youtube-nocookie.com") && pathname.startsWith("/embed/");
        const isVimeoEmbed = hostname === "player.vimeo.com" && pathname.startsWith("/video/");
        return isYouTubeEmbed || isVimeoEmbed;
    }
    catch (_a) {
        return false;
    }
}
export function toEmbedUrl(url) {
    try {
        const { protocol, hostname, pathname, searchParams, } = new URL(url);
        if (protocol !== "https:") {
            return null;
        }
        if (!ALLOWED_VIDEO_HOSTS.has(hostname)) {
            return null;
        }
        // YouTube watch URL: youtube.com/watch?v=ID
        const ytId = searchParams.get("v");
        if (ytId) {
            return `${YOUTUBE_NOCOOKIE_EMBED_BASE}${ytId}`;
        }
        // YouTube short URL: youtu.be/ID or www.youtu.be/ID
        if (hostname === "youtu.be" || hostname === "www.youtu.be") {
            const id = pathname.slice(1);
            if (id) {
                return `${YOUTUBE_NOCOOKIE_EMBED_BASE}${id}`;
            }
        }
        // YouTube shorts: youtube.com/shorts/ID
        if ((hostname === "www.youtube.com" || hostname === "youtube.com")
            && pathname.startsWith("/shorts/")) {
            const id = pathname.replace("/shorts/", "");
            if (id) {
                return `${YOUTUBE_NOCOOKIE_EMBED_BASE}${id}`;
            }
        }
        // Already a YouTube embed (normalise to nocookie)
        if ((hostname === "www.youtube.com" || hostname === "youtube.com"
            || hostname === "www.youtube-nocookie.com" || hostname === "youtube-nocookie.com")
            && pathname.startsWith("/embed/")) {
            const id = pathname.replace("/embed/", "");
            return `${YOUTUBE_NOCOOKIE_EMBED_BASE}${id}`;
        }
        // Vimeo: vimeo.com/ID or www.vimeo.com/ID
        const vimeoMatch = pathname.match(/^\/(\d+)/);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }
        // Already a Vimeo embed
        if (hostname === "player.vimeo.com" && pathname.startsWith("/video/")) {
            return url;
        }
        return null;
    }
    catch (_a) {
        return null;
    }
}
// Content copied from PDFs and some other sources uses non-breaking spaces
// (U+00A0) instead of regular spaces, forcing mid-word line breaks once
// pasted text overflows a line. Called from the `transformPasted` editor
// prop, which only ever receives an already-parsed, already-decoded Slice —
// so this just needs to check for the literal character, not any
// HTML-entity encoding of it.
export function stripNonBreakingSpaces(fragment) {
    const children = [];
    fragment.forEach((node) => {
        if (node.isText && node.text) {
            children.push(node.type.schema.text(node.text.replace(/\u00A0/g, " "), node.marks));
        }
        else if (node.content.childCount > 0) {
            children.push(node.copy(stripNonBreakingSpaces(node.content)));
        }
        else {
            children.push(node);
        }
    });
    return Fragment.fromArray(children);
}
